import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '../../../components/Combobox';
import type { ReplyContact } from './api';
import { isAddress } from './recipients';

interface RecipientFieldProps {
  id: string;
  label: string;
  addresses: string[];
  onChange: (addresses: string[]) => void;
  /** Die Ansprechpartner des Ticket-Kunden -- und nur dieses Kunden. */
  contacts: ReplyContact[];
  /** Adressen, die in einem anderen Feld schon stehen und hier nicht erscheinen. */
  taken: string[];
  /** Der getippte, noch nicht uebernommene Text -- er zaehlt beim Absenden mit. */
  pending: string;
  onPending: (text: string) => void;
}

/**
 * Eine Empfaengerzeile der Antwort -- An, CC oder BCC (F3-22).
 *
 * **Vorschlaege nur vom Kunden des Tickets.** Eine Liste aller Kontakte des
 * Hauses waere die Einladung, die Antwort an den falschen Kunden in Kopie zu
 * setzen. Wer jemand anderen meint, tippt die Adresse.
 *
 * **Ein getippter Text gilt, auch ohne Eingabetaste.** Wer eine Adresse
 * eintippt und dann "Senden" klickt, hat sie gemeint; der Verfasser nimmt
 * ``pending`` beim Absenden mit. Eine unvollstaendige Adresse sagt das Feld,
 * statt sie still zu verwerfen.
 */
export function RecipientField({
  id,
  label,
  addresses,
  onChange,
  contacts,
  taken,
  pending,
  onPending,
}: RecipientFieldProps) {
  const { t } = useTranslation();
  // Das Feld leert sich nach jeder Wahl; ein Zaehler erzwingt das, weil der
  // Wert des Auswahlfelds sonst auf dem letzten Eintrag stehen bliebe.
  const [round, setRound] = useState(0);

  const vergeben = new Set([...addresses, ...taken].map((address) => address.toLowerCase()));
  const options: ComboboxOption[] = contacts
    .filter((contact) => !vergeben.has(contact.email.toLowerCase()))
    .map((contact) => ({
      id: contact.email,
      label: contact.name || contact.email,
      hint: contact.email,
      meta: contact.position ?? undefined,
    }));

  const add = (raw: string) => {
    const address = raw.trim().toLowerCase();
    if (!isAddress(address) || vergeben.has(address)) return;
    onChange([...addresses, address]);
    onPending('');
    setRound((value) => value + 1);
  };

  const invalid = pending.trim() !== '' && !isAddress(pending);

  return (
    <div className="composer__recipients-row">
      <label className="composer__recipients-label" htmlFor={id}>
        {label}
      </label>
      <div className="composer__recipients-field">
        {addresses.map((address) => (
          <button
            key={address}
            type="button"
            className="kb-chip"
            aria-label={t('articles.removeRecipient', { address })}
            onClick={() => {
              onChange(addresses.filter((entry) => entry !== address));
            }}
          >
            {contacts.find((contact) => contact.email === address)?.name ?? address}
            <span className="kb-chip__remove" aria-hidden="true">
              ×
            </span>
          </button>
        ))}
        <Combobox
          key={round}
          id={id}
          ariaLabel={label}
          value={null}
          options={options}
          allowFreeText
          clearable={false}
          className="composer__recipients-input"
          placeholder={t('articles.recipientPlaceholder')}
          onChange={(option) => {
            if (option) add(option.id || option.label);
          }}
          onInput={onPending}
        />
      </div>
      {invalid ? (
        <span className="field__error composer__recipients-error" role="alert">
          {t('articles.recipientInvalid')}
        </span>
      ) : null}
    </div>
  );
}
